import { Line } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'

import { Trans } from '@/components'
import { ChartType, enumToChartType } from '@/models/chart'
import { Instance } from '@/models/instance'
import { mb2GbString } from '@/utils/file'
import { cn } from '@/utils/style'

export interface MonitorItemData {
  type: ChartType
  title: React.ReactElement
  description: React.ReactElement
  realtime: React.ReactElement
  values: MonitorValue[]
  isEmpty: boolean
}

interface MonitorValue {
  time: string
  value: number | undefined
  unit: string
}

// Title
function getChartTitle(chartType: ChartType): string[] {
  const nameMap = {
    [ChartType.CPU]: ['CPU Usage'],
    [ChartType.Memory]: ['Memory Usage'],
    [ChartType.Disk]: ['Disk Usage'],
    [ChartType.Bandwidth]: ['Bandwidth'],
    [ChartType.GPUCore]: ['GPU Core', '(per GPU)'],
    [ChartType.GPURAM]: ['GPU RAM', '(per GPU)'],
  } as any
  return nameMap[chartType] || ['Unknown']
}

const renderChartTitle = (type: ChartType) => {
  const titles = getChartTitle(type)
  return (
    <div className="flex t-sb">
      <div className=" text-primary">{titles[0]}</div>
      <div className=" text-tertiary">&nbsp;{titles[1]}</div>
    </div>
  )
}

// Desc
function getChartDescription(instance: Instance, chartType: ChartType): string[] {
  const nameMap = {
    [ChartType.CPU]: [`${instance.hardware.machine.cpuName}`],
    [ChartType.Memory]: ['(GB)'],
    [ChartType.Disk]: ['(GB)'],
    [ChartType.Bandwidth]: [],
    [ChartType.GPUCore]: [`${instance.hardware.gpu.gpuName}`],
    [ChartType.GPURAM]: ['(GB)'],
  } as any
  return nameMap[chartType] || ['']
}

const renderChartDescription = (instance: Instance, type: ChartType) => {
  const descs = getChartDescription(instance, type)
  return (
    <div>
      <div className="t-xs font-medium text-secondary">{descs[0]}</div>
    </div>
  )
}

// Realtime
function getChartRealtime(instance: Instance, chartType: ChartType, item: MonitorValue): string[] {
  const nameMap = {
    [ChartType.CPU]: [`${item.value || 0}%`],
    [ChartType.Memory]: [
      `${convertToGB(item.value || 0, item.unit)}`,
      `/${mb2GbString(instance.hardware.machine.totalRAMMB)}`,
    ],
    [ChartType.Disk]: [
      `${convertToGB(item.value || 0, item.unit)}`,
      `/${instance.hardware.machine.diskGB}GB`,
    ],
    [ChartType.Bandwidth]: [`${item.value}`],
    [ChartType.GPUCore]: [`${item.value || 0}%`],
    [ChartType.GPURAM]: [
      `${convertToGB(item.value || 0, item.unit)}`,
      `/${mb2GbString(instance.hardware.gpu.gpuRamMB)}`,
    ],
  } as any
  return nameMap[chartType] || ['']
}

const renderChartRealtime = (instance: Instance, type: ChartType, item: MonitorValue) => {
  const descs = getChartRealtime(instance, type, item)
  return (
    <div className="bg-panel-gray-100 flex items-center py-2 px-3 t-bold gap-3">
      <div className="flex items-center">
        <div className={cn('t-sb', ' text-primary')}>{descs[0]}</div>
        <div className={cn('t-sb', ' text-tertiary')}>{descs[1]}</div>
      </div>
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const buildChartItemData = (
  instance: Instance,
  item: any,
  timePoints: dayjs.Dayjs[],
): MonitorItemData => {
  const chartType = enumToChartType(item.chartType)
  let data: { time: dayjs.Dayjs; value?: number }[]
  let isEmpty = false

  if (!item.result[0]?.matrixArray || item.result[0].matrixArray.length === 0) {
    data = timePoints.map((time) => ({
      time,
    }))
    isEmpty = true
  } else {
    data = item.result[0].matrixArray.map((v: any) => ({
      time: dayjs(v?.matrix?.[0] * 1000),
      value: v?.matrix?.[1],
    }))
  }
  const max = data.reduce((total: number, next: any) => {
    if (next.value > total) {
      return next.value
    }
    return total
  }, 0)
  const fmax = formatValue(chartType, max)
  const lastData = data.slice(-1)[0]
  return {
    title: renderChartTitle(chartType),
    description: renderChartDescription(instance, chartType),
    realtime: renderChartRealtime(instance, chartType, convertToMonitorValue(lastData, fmax)),
    type: chartType,
    values: data?.map((v) => convertToMonitorValue(v, fmax)),
    isEmpty,
  }
}

function convertToMonitorValue(
  v: { time: dayjs.Dayjs; value?: number },
  fmax: { value: number; unit: string },
): MonitorValue {
  return {
    time: v.time.format(),
    value: v.value ? formatValueV2(v.value, fmax.unit) : undefined,
    unit: fmax.unit,
  }
}

function formatValueV2(value: number, unit: string) {
  if (unit == '%') return parseFloat(value.toFixed(2))
  if (unit == 'B') return value
  if (unit == 'KB') return parseFloat((value / 1024).toFixed(2))
  if (unit == 'MB') return parseFloat((value / 1024 / 1024).toFixed(2))
  if (unit == 'GB') return parseFloat((value / 1024 / 1024 / 1024).toFixed(2))
  if (unit == 'TB') return parseFloat((value / 1024 / 1024 / 1024 / 1024).toFixed(2))
}

function convertToGB(value: number, unit: string) {
  if (unit == 'B') return parseFloat((value / 1024 / 1024 / 1024).toFixed(2))
  if (unit == 'KB') return parseFloat((value / 1024 / 1024).toFixed(2))
  if (unit == 'MB') return parseFloat((value / 1024).toFixed(2))
  if (unit == 'GB') return value
  if (unit == 'TB') return value * 1024
}

function formatValue(chartType: ChartType, value: number): { value: number; unit: string } {
  switch (chartType) {
    case ChartType.CPU:
      return { value: value, unit: '%' }
    case ChartType.Memory:
      return bytesToSize(value)
    case ChartType.Disk:
      return bytesToSize(value)
    case ChartType.Bandwidth:
      return { value: value, unit: 'G' }
    case ChartType.GPUCore:
      return { value: value, unit: '%' }
    default:
      return bytesToSize(value)
  }
}

function bytesToSize(bytes: number) {
  if (bytes === 0) return { value: 0, unit: 'B' }
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return {
    value: parseFloat((bytes / Math.pow(k, i)).toFixed(2)),
    unit: sizes[i],
  }
}

export function Chart(props: { value: MonitorItemData }) {
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()

  useEffect(() => {
    const { type, isEmpty } = props.value
    const values = props.value.values || []
    const unit = values[0]?.unit
    const usage = unit == '%'
    if (values.length > 0) {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
      lineRef.current = new Line(id, {
        data: values,
        padding: 'auto',
        xField: 'time',
        yField: 'value',

        xAxis: {
          tickCount: 7,
          label: {
            formatter: (v) => {
              return `${dayjs(v).format('HH:mm')}`
            },
          },
          tickLine: {
            length: -5,
          },
          grid: isEmpty
            ? null
            : {
                line: {
                  type: 'line',
                  style: {
                    lineWidth: 1,
                    color: '#EEEFF1',
                    opacity: 0.1,
                  },
                },
              },
        },
        yAxis: {
          tickCount: usage ? 6 : undefined,
          label: {
            formatter: (v) => {
              return `${v}${unit}`
            },
          },
          min: usage ? 0 : undefined,
          max: usage ? 100 : undefined,
          grid: isEmpty
            ? null
            : {
                line: {
                  type: 'line',
                  style: {
                    lineWidth: 1,
                    color: '#EEEFF1',
                    opacity: 0.3,
                  },
                },
              },
        },
        area: {
          style: {
            fill: 'l(270) 0:#365fef00 1:#365fef80',
          },
        },
        tooltip: {
          showMarkers: false,

          customContent(title, data) {
            const item = data[0]
            if (!item) return ''
            return `<div>
            <div>${title}<div>
            <div>${item.name}: ${item.value || 0}${item.unit}<div>
            </div>`
          },
          domStyles: {
            'g2-tooltip': {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              color: 'white',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: '500',
              textAlign: 'left',
            },
          },
          fields: ['time', 'value'],
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: {
                lineWidth: 1,
                lineDash: [5, 3],
                color: '#B4B6BE',
              },
            },
          },
          formatter: (datum) => {
            return {
              ...datum,
              title: dayjs(datum.time).format('YYYY-MM-DD HH:mm:ss'),
              name: getChartTitle(type)[0],
              value: datum.value,
              unit: unit,
            }
          },
        },
        smooth: true,
        lineStyle: {
          lineWidth: 1,
          stroke: '#365FEF',
        },
      })
      lineRef.current.render()
    }
    return () => {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
    }
  }, [JSON.stringify(props.value)])

  return (
    <div className="h-[160px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden mt-4 text-center text-tertiary t-sm  absolute"
      ></div>
      {props.value?.isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
        </div>
      )}
    </div>
  )
}
